import React, { createContext, useState, useContext, useEffect } from 'react';
import supabase from '../supabaseClient';
import { updateTeacherLastActive } from '../utils/teacherActivity';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const isSigningOutPending = React.useRef(false);

  const checkUserRole = async (userId) => {
    try {
      // First check if user is a super admin
      const { data: superAdminData } = await supabase
        .from('super_admins')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
      
      if (superAdminData) {
        return 'super_admin';
      }

      // Check if user is an admin
      const { data: adminData } = await supabase
        .from('admins')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
      
      if (adminData) {
        return 'admin';
      }

      // Check if user is a teacher
      const { data: teacherData } = await supabase
        .from('teachers')
        .select('id')
        .eq('id', userId)
        .maybeSingle();
      
      if (teacherData) {
        return 'teacher';
      }
      
      // Check pending users if not found in other tables
      const { data: pendingUser } = await supabase
        .from('pending_users')
        .select('user_type')
        .eq('id', userId)
        .maybeSingle();
        
      return pendingUser?.user_type || null;
    } catch (error) {
      console.error('Error checking user role:', error);
      return null;
    }
  };

  const updateUserSession = async (session) => {
    // If we're in the process of signing out a pending user, only process if session is null (sign out completed)
    if (isSigningOutPending.current && session?.user) {
      return;
    }

    if (session?.user) {
      // First check if user is an approved user (super_admin, admin, or teacher)
      // This ensures approved users aren't affected even if they have a pending_users record
      const { data: superAdminData } = await supabase
        .from('super_admins')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
      
      const { data: adminData } = await supabase
        .from('admins')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
      
      const { data: teacherData } = await supabase
        .from('teachers')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
      
      // If user is approved (exists in any approved table), proceed with authentication
      if (superAdminData || adminData || teacherData) {
        setUser(session.user);
        const role = await checkUserRole(session.user.id);
        setUserRole(role);
        setLoading(false);
        return;
      }
      
      // User is not approved - check if they are pending or rejected
      const { data: pendingUser } = await supabase
        .from('pending_users')
        .select('status')
        .eq('id', session.user.id)
        .maybeSingle();
      
      if (pendingUser && (pendingUser.status === 'pending' || pendingUser.status === 'rejected')) {
        // User is pending or rejected - sign them out silently without setting user state
        // This prevents them from being routed anywhere
        isSigningOutPending.current = true;
        await supabase.auth.signOut();
        setUser(null);
        setUserRole(null);
        isSigningOutPending.current = false;
        setLoading(false);
        return;
      }
      
      // User is not in any table (edge case) - proceed with normal authentication
      setUser(session.user);
      const role = await checkUserRole(session.user.id);
      setUserRole(role);
    } else {
      setUser(null);
      setUserRole(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Check for recovery token on page load
    const handleRecoveryToken = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const type = params.get('type');

      if (type === 'recovery' && token) {
        try {
          // Verify the recovery token
          const { error } = await supabase.auth.verifyOtp({
            token,
            type: 'recovery'
          });

          if (error) throw error;

          // Set password reset state to true and navigate
          setIsPasswordReset(true);
          window.location.href = '/reset-password';
        } catch (error) {
          console.error('Error verifying recovery token:', error);
          setIsPasswordReset(false);
          window.location.href = '/login';
        }
      }
    };

    handleRecoveryToken();

    // Existing session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      updateUserSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordReset(true);
        return;
      } else if (event === 'SIGNED_OUT') {
        setIsPasswordReset(false);
        // Reset the flag when explicitly signed out
        isSigningOutPending.current = false;
      }
      
      updateUserSession(session);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email, password, userType, userData) => {
    try {
      // First check if the email is already registered
      const { isPending, status, error: checkError } = await checkPendingStatus(email);
      
      if (checkError) {
        console.error('Error checking existing account:', checkError);
      }
      
      if (isPending) {
        if (status === 'pending') {
          return { data: null, error: new Error('This email is already registered and pending approval.') };
        } else if (status === 'rejected') {
          return { data: null, error: new Error('This email registration was previously rejected. Please contact an administrator.') };
        } else {
          return { data: null, error: new Error('This email is already registered.') };
        }
      }
      
      // If not pending, proceed with registration
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Set auth metadata to help with RLS policies
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { 
            user_type: userType,
            first_name: userData.first_name,
            last_name: userData.last_name
          }
        });
        
        if (metadataError) {
          console.error("Error updating user metadata:", metadataError);
        }
        
        // Then try to insert the pending user
        try {
          const { error: pendingError } = await supabase
            .from('pending_users')
            .insert([{
              id: data.user.id,
              email: email.toLowerCase().trim(), // Normalize email
              first_name: userData.first_name,
              last_name: userData.last_name,
              phone: userData.phone,
              user_type: userType,
            }]);

          if (pendingError) {
            console.error("Error creating pending user:", pendingError);
            throw pendingError;
          }
        } catch (pendingError) {
          console.error("Error creating pending user:", pendingError);
          throw pendingError;
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      // Normalize email
      const normalizedEmail = email.toLowerCase().trim();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // First check if user is a super admin
        const { data: superAdminData } = await supabase
          .from('super_admins')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();
        
        if (superAdminData) {
          setUserRole('super_admin');
          return { data, error: null };
        }

        // Then check if user is an admin
        const { data: adminData } = await supabase
          .from('admins')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();
        
        if (adminData) {
          setUserRole('admin');
          return { data, error: null };
        }

        // Check if user is a teacher
        const { data: teacherData } = await supabase
          .from('teachers')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();
        
        if (teacherData) {
          setUserRole('teacher');
          // Update teacher's last_active timestamp on login
          await updateTeacherLastActive(data.user.id);
          return { data, error: null };
        }
        
        // Finally check pending status
        const { data: pendingUser, error: pendingError } = await supabase
          .from('pending_users')
          .select('status, user_type')
          .eq('id', data.user.id)
          .maybeSingle();

        if (pendingError) throw pendingError;

        if (pendingUser.status === 'pending') {
          await supabase.auth.signOut();
          throw new Error('Your registration is pending approval. Please wait for an admin to approve your account.');
        }

        if (pendingUser.status === 'rejected') {
          await supabase.auth.signOut();
          throw new Error('Your registration has been rejected. Please contact the administrator for more information.');
        }
        
        setUserRole(pendingUser.user_type);
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUserRole(null);
      setIsPasswordReset(false);
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  // Check if an email is already registered and pending
  const checkPendingStatus = async (email) => {
    try {
      // Check in pending_users table using case-insensitive email comparison
      const { data: pendingData, error: pendingError } = await supabase
        .from('pending_users')
        .select('status, email')
        .ilike('email', email)
        .limit(1);
      
      if (pendingError) {
        console.error('Error checking pending status:', pendingError);
        return { isPending: false, error: pendingError };
      }
      
      // If we found a matching email in pending_users
      if (pendingData && pendingData.length > 0) {
        return { 
          isPending: true, 
          status: pendingData[0].status,
          email: pendingData[0].email
        };
      }
      
      // No matching records in pending_users
      return { isPending: false };
    } catch (error) {
      console.error('Error checking pending status:', error);
      return { isPending: false, error };
    }
  };

  const value = {
    user,
    userRole,
    loading,
    signUp,
    signIn,
    signOut,
    checkPendingStatus,
    isPasswordReset,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 